import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, OutlinedInput, InputAdornment, IconButton, Button, CircularProgress } from '@mui/material';
import { MobileSVG, NaturaBannerSVG, ShowPasswordSVG, ShowPasswordOffSVG } from '../../components/svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import '../../styles/sass/auth/passwordForm.scss';
import { useTranslation, Trans } from 'react-i18next';
import LanguageSelector from '../../components/languageSelector';

export default function PasswordForm ({ isLoading, onClick, type, password, confirmPassword, setPassword, setConfirmPassword, subtitle }) {
    const { t } = useTranslation();
    // State

    // Visible Passwords state
    const [isVisible, setIsVisible] = useState({
        password: false,
        confirmPassword: false
    });
    const [validations, setValidations] = useState({});

    // Functions

    const hasLength = new RegExp('^(?=.{8,})');
    const hasLower = new RegExp('^(?=.*[a-z])');
    const hasUpper = new RegExp('^(?=.*[A-Z])');
    const hasNum = new RegExp('^(?=.*[0-9])');
    const hasSpecial = new RegExp('^(?=.*[-+!@#$%^&*])');
    const unequalPassword = password && confirmPassword && password !== confirmPassword;

    const onChangeValidation = (prop) => (e) => {
        const pass = e.target.value;

        switch (prop) {
            case 'password':
                setPassword(pass);
                setValidations({
                    hasLength: hasLength.test(pass),
                    hasLower: hasLower.test(pass),
                    hasUpper: hasUpper.test(pass),
                    hasNum: hasNum.test(pass),
                    hasSpecial: hasSpecial.test(pass),
                    isValid: (hasLength.test(pass) && hasLower.test(pass) && hasUpper.test(pass) && hasNum.test(pass) && hasSpecial.test(pass))
                });
                break;
            case 'confirmPassword':
                setConfirmPassword(pass);
                break;
            default:
        }
    };

    useEffect(() => {
        setValidations({
            hasLength: false,
            hasLower: false,
            hasUpper: false,
            hasNum: false,
            hasSpecial: false,
            isValid: false
        });
    }, []);

    return (
        <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="stretch"
        >

            <Box className="password_header--banner">
                <NaturaBannerSVG />
                <Box position="absolute" right="20px" top="50%" >
                    <LanguageSelector fill="#EFFEF8"/>

                </Box>
            </Box>

            <Box className="password_container--main">

                <Box width='400px' display='flex' flexDirection='column' gap={'32px'}>
                    <Box>
                        <Typography className='password_creator--title'>
                            {t('recover.title')}{' '}
                            <span style={{ color: '#F04006' }}>{t('recover.title2')}</span>
                        </Typography>
                        <Typography className='password_creator--subtitle'>{subtitle}</Typography>
                        {type === 'recover' && <Typography className='password_creator--subtitle helper'><Trans i18nKey={'recover.helperText'} /></Typography>
                        }
                    </Box>
                    <Box width='400' >
                        <Typography className='password_creator--formtitle'>{t('recover.inputs.password.label')}</Typography>
                        <FormControl required variant="outlined" fullWidth>
                            <OutlinedInput
                                sx={{
                                    '& .MuiOutlinedInput-input::placeholder': {
                                        color: 'disabled.950',
                                        opacity: 1
                                    }
                                }}
                                onChange={onChangeValidation('password')}
                                style={{ borderRadius: '4px', marginTop: '6px', height: '40px', width: '100%' }}
                                color="secondary"
                                placeholder={t('recover.inputs.password.placeholder')}
                                type={isVisible.password ? 'text' : 'password'}
                                value={password}
                                endAdornment={<InputAdornment position="end" >
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setIsVisible({ ...isVisible, password: !isVisible.password })}
                                        edge="end"
                                        size="large">
                                        {isVisible.password ? <ShowPasswordSVG fill="#F04006" /> : <ShowPasswordOffSVG fill="#F04006" />}
                                    </IconButton>
                                </InputAdornment>}
                            />
                        </FormControl>
                    </Box>
                    <Box width='400px' sx={{ position: 'relative ' }}>
                        <Typography className='password_creator--formtitle'>{t('recover.inputs.confirmPassword.label')}</Typography>
                        <FormControl required variant="outlined" fullWidth>
                            <OutlinedInput
                                sx={{
                                    '& .MuiOutlinedInput-input::placeholder': {
                                        color: 'disabled.950',
                                        opacity: 1
                                    }
                                }}
                                onChange={onChangeValidation('confirmPassword')}
                                style={{ borderRadius: '4px', marginTop: '6px', height: '40px', width: '100%' }}
                                color="secondary"
                                error={unequalPassword}
                                placeholder={t('recover.inputs.confirmPassword.placeholder')}
                                type={isVisible.confirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                endAdornment={<InputAdornment position="end" >
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setIsVisible({ ...isVisible, confirmPassword: !isVisible.confirmPassword })}
                                        edge="end"
                                        size="large">
                                        {isVisible.confirmPassword ? <ShowPasswordSVG fill="#F04006" /> : <ShowPasswordOffSVG fill="#F04006" />}
                                    </IconButton>
                                </InputAdornment>}
                            />
                            {
                                unequalPassword && (
                                    <Typography className='password_creator--error'>{t('recover.changePassword.error')}</Typography>

                                )
                            }
                        </FormControl>
                    </Box>
                    <Box display='flex' gap='17px' >
                        <Box>
                            <Typography variant='body2' className='password_conditions--title'>
                                {t('recover.help.title')}
                            </Typography>
                            <Box
                                className="password_conditions--list"
                            >

                                <Box className="password_conditions--col">
                                    <Box className="password_conditions--item">
                                        <CheckCircleIcon fontSize='small' color={validations.hasLength ? 'primary' : 'disabled'} />
                                        <Typography className='password_conditions--items'>{t('recover.help.list.0')}</Typography>
                                    </Box>
                                    <Box className="password_conditions--item">
                                        <CheckCircleIcon fontSize='small' color={validations.hasLower ? 'primary' : 'disabled'} />
                                        <Typography className='password_conditions--items'>{t('recover.help.list.1')}</Typography>
                                    </Box>
                                    <Box className="password_conditions--item">
                                        <CheckCircleIcon fontSize='small' color={validations.hasUpper ? 'primary' : 'disabled'} />
                                        <Typography className='password_conditions--items'>{t('recover.help.list.2')}</Typography>
                                    </Box>
                                </Box>

                                <Box className="password_conditions--col">
                                    <Box className="password_conditions--item">
                                        <CheckCircleIcon fontSize='small' color={validations.hasNum ? 'primary' : 'disabled'} />
                                        <Typography className='password_conditions--items'>{t('recover.help.list.3')}</Typography>
                                    </Box>
                                    <Box className="password_conditions--item">
                                        <CheckCircleIcon fontSize='small' color={validations.hasSpecial ? 'primary' : 'disabled'} />
                                        <Typography className='password_conditions--items'>{t('recover.help.list.4')}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                    </Box>
                    <Box style={{ width: '4.5vw' }}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            className='password_button--submit'
                            onClick={onClick}
                            disabled={(!validations.isValid || (password !== confirmPassword))}
                        >
                            {(isLoading
                                ? <CircularProgress size={20} style={{ color: 'white' }} />
                                : t('buttons.confirm'))}
                        </Button>
                    </Box>
                </Box>

                <Box mt="20px">
                    <MobileSVG />
                </Box>

            </Box>
        </Box>
    );
};
